import { ThemeEvents } from '@theme/events';

class EcomMindsOutletProgress extends HTMLElement {
  static productCache = new Map();

  connectedCallback() {
    this.targets = [+this.dataset.q1, +this.dataset.q2];
    this.discounts = [+this.dataset.d1, +this.dataset.d2];
    this.campaignTag = (this.dataset.campaignTag || 'Outlet').trim().toLocaleLowerCase('tr-TR');
    this.abortController = new AbortController();
    this.renderToken = 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate, {
      signal: this.abortController.signal,
    });

    this.position();
    this.loadCart();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  position() {
    const max = this.targets[1] || 1;
    this.targets.forEach((value, index) => {
      const x = `${Math.max(0, Math.min(100, (value / max) * 100))}%`;
      this.querySelector(`[data-m="${index + 1}"]`)?.style.setProperty('--x', x);
      this.querySelector(`[data-l="${index + 1}"]`)?.style.setProperty('--x', x);
    });
  }

  onCartUpdate = (event) => {
    const cart = event.detail?.resource;
    if (cart?.items) this.updateFromCart(cart);
    else this.loadCart();
  };

  async loadCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      await this.updateFromCart(await response.json());
    } catch (error) {
      console.error('[EcomMinds Outlet Progress] cart load failed', error);
    }
  }

  async updateFromCart(cart) {
    const token = ++this.renderToken;
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const eligibleFlags = await Promise.all(items.map((item) => this.isEligible(item)));
    if (token !== this.renderToken) return;

    const eligibleQuantity = items.reduce((sum, item, index) => {
      if (!eligibleFlags[index]) return sum;
      return sum + Math.max(0, Number(item.quantity) || 0);
    }, 0);

    this.render(eligibleQuantity);
  }

  async isEligible(item) {
    const handle = item?.handle;
    if (!handle) return false;

    const cacheKey = `${handle}::${this.campaignTag}`;
    if (EcomMindsOutletProgress.productCache.has(cacheKey)) {
      return EcomMindsOutletProgress.productCache.get(cacheKey);
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const product = await response.json();
      const tags = Array.isArray(product.tags)
        ? product.tags
        : String(product.tags || '').split(',');

      const eligible = tags.some(
        (tag) => String(tag).trim().toLocaleLowerCase('tr-TR') === this.campaignTag
      );

      EcomMindsOutletProgress.productCache.set(cacheKey, eligible);
      return eligible;
    } catch (error) {
      console.error('[EcomMinds Outlet Progress] product eligibility failed', handle, error);
      EcomMindsOutletProgress.productCache.set(cacheKey, false);
      return false;
    }
  }

  render(quantity) {
    quantity = Math.max(0, Number(quantity) || 0);

    const max = this.targets[1] || 1;
    const fill = this.querySelector('[data-fill]');
    if (fill) fill.style.width = `${Math.min(100, (quantity / max) * 100)}%`;

    const count = this.querySelector('[data-outlet-count]');
    if (count) count.textContent = String(quantity);

    let earned = -1;
    let next = -1;

    this.targets.forEach((target, index) => {
      const reached = quantity >= target;

      if (reached) earned = index;
      else if (next < 0) next = index;

      this.querySelector(`[data-m="${index + 1}"]`)?.classList.toggle('hit', reached);
      this.querySelector(`[data-l="${index + 1}"]`)?.classList.toggle('hit', reached);
    });

    const message = this.querySelector('[data-msg]');
    if (!message) return;

    if (next < 0) {
      message.innerHTML = `Tebrikler • <strong>+%${this.discounts[1]} indirim</strong> seviyesine ulaştın`;
      return;
    }

    const remaining = Math.max(0, this.targets[next] - quantity);

    if (earned < 0) {
      message.innerHTML =
        `<strong>${remaining} Outlet ürünü daha ekle</strong>, +%${this.discounts[next]} indirime ulaş`;
    } else {
      message.innerHTML =
        `<strong>+%${this.discounts[earned]} indirimi kazandın</strong> • ` +
        `${remaining} Outlet ürünü daha ekle, +%${this.discounts[next]}'a ulaş`;
    }
  }
}

if (!customElements.get('ecomminds-outlet-progress')) {
  customElements.define('ecomminds-outlet-progress', EcomMindsOutletProgress);
}
